import Layout from "../components/Layout";
import { useState } from "react";
import { Copy, Check, Zap, ArrowRight, Code2, Bot, Database } from "lucide-react";

function CodeBlock({ code, lang = "json" }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative bg-[#111827] rounded-md overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800/50 border-b border-gray-700">
        <span className="text-xs text-gray-400 font-mono uppercase">{lang}</span>
        <button onClick={copy} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors">
          {copied ? <><Check className="w-3 h-3 text-green-400" /> Kopyalandı</> : <><Copy className="w-3 h-3" /> Kopyala</>}
        </button>
      </div>
      <pre className="p-4 text-sm text-gray-100 font-mono overflow-x-auto whitespace-pre-wrap leading-relaxed">{code}</pre>
    </div>
  );
}

function Section({ num, title, icon: Icon, children }) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 bg-[#DC2626] rounded-md flex items-center justify-center text-white text-sm font-bold shrink-0"
          style={{ fontFamily: "Barlow Condensed, sans-serif" }}>{num}</div>
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-gray-500" strokeWidth={1.5} />
          <h2 className="text-xl font-bold uppercase text-gray-900"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}>{title}</h2>
        </div>
      </div>
      <div className="pl-11">{children}</div>
    </div>
  );
}

export default function IntegrationGuidePage() {
  // Dinamik URL: hangi domain'den açılırsa onu göster — domain değişince otomatik güncellenir
  const apiUrl = window.location.origin;

  const priceRequestBody = `// TEK API ÇAĞRISI — HER ŞEY BİR ARADA
POST ${apiUrl}/api/bot
Authorization: Bearer YOUR_API_KEY

{
  "model_text": "newlight 125",   // müşterinin yazdığı (veya)
  "model_key":  "newlight125",    // stabil key (önerilen)
  "model_id":   "uuid...",        // Kommo'da kaydedilmiş ID (en hızlı)
  "brand":      "KUBA",           // opsiyonel - marka filtresi
  "channel":    "whatsapp",       // opsiyonel - kanal bilgisi
  "include_context": true         // false = sadece fiyat, context yok
}`;

  const priceSuccessResp = `// price.status = "success"
{
  "price": {
    "status": "success",
    "item_id": "e261-xxxx",
    "model_key": "newlight125pro",    // Stabil — liste değişse de geçerli
    "brand": "KUBA",
    "model": "NEWLIGHT 125 PRO",
    "liste_fiyati": 73603,
    "ucar_nakit": 67000,
    "taksit_3": 67000,
    "taksit_6": 71500,
    "reply_text": "✅ NEWLIGHT 125 PRO | Liste: 73.603 TL | Nakit: 67.000 TL ..."
  },
  "chatbot_context": {
    "welcome":          [{"title": "Karşılama", "content": "Merhaba! ..."}],
    "installment_info": [{"title": "Taksit", "content": "3 ve 6 taksit..."}],
    "faq":              [{"title": "Garanti?", "content": "2 yıl ..."}],
    "service_info":     [...],
    "custom_note":      [...]
  },
  "active_list": "Şubat 2025 - KUBA + RKS",
  "response_time_ms": 45
}`;

  const priceMultipleResp = `// price.status = "multiple_matches"
{
  "price": {
    "status": "multiple_matches",
    "count": 2,
    "matches": [
      {"item_id": "aaa", "model": "NEWLIGHT 50", "model_key": "newlight50"},
      {"item_id": "bbb", "model": "NEWLIGHT 125 PRO", "model_key": "newlight125pro"}
    ],
    "reply_text": "newlight için 2 model bulundu:\\n1. NEWLIGHT 50\\n2. NEWLIGHT 125 PRO"
  },
  "chatbot_context": { ... },
  "active_list": "Şubat 2025 - KUBA + RKS"
}`;

  const priceNotFoundResp = `{
  "status": "not_found",
  "reply_text": "Modeli bulamadım. Lütfen modeli listeden seçin veya örnek: 'VRS 125' yazın."
}`;

  const priceByIdBody = `{
  "model_id": "e261-xxxx-xxxx",   // item_id'yi bir kez öğrenince bunu kullan
  "channel": "whatsapp"
}`;

  const n8nFlow = `KOMMO SALESBOT → N8N/MAKE AKIŞI  (TEK API)

1. SALESBOT TETİKLEYİCİ
   Lead alanı: "model_key" → {{model_key}}   (en güvenilir)
   Lead alanı: "model_id"  → {{model_id}}    (boş olabilir)
   Lead alanı: "model_adi" → {{model_adi}}   (müşteri yazdığı)

2. HTTP REQUEST NODE — tek çağrı, her şey gelir
   POST ${apiUrl}/api/bot
   Header: Authorization: Bearer YOUR_API_KEY
   Body:
   {
     "model_key":  "{{model_key}}",     // varsa
     "model_id":   "{{model_id}}",      // varsa
     "model_text": "{{model_adi}}",     // yoksa metin kullan
     "channel": "whatsapp",
     "include_context": true
   }

3. SWITCH NODE — price.status değerine göre:
   │
   ├── "success" →
   │   a) Müşteriye: {{price.reply_text}} gönder
   │   b) Lead'e kaydet: model_key={{price.model_key}}
   │   c) Lead'e kaydet: model_id={{price.item_id}}
   │
   ├── "multiple_matches" →
   │   a) Müşteriye: {{price.reply_text}} gönder (seçim listesi)
   │   b) Cevaba göre model_key kaydet, tekrar sorgula
   │
   └── "not_found" →
       a) Müşteriye: {{price.reply_text}} gönder
       b) Satış danışmanına yönlendir`;

  const kommoSalesbot = `KOMMO SALESBOT HTTP REQUEST AYARLARI

URL:    POST ${apiUrl}/api/bot
Header: Authorization: Bearer YOUR_API_KEY
Body:
  {
    "model_key":      "{{lead.custom_field.model_key}}",
    "model_id":       "{{lead.custom_field.model_id}}",
    "model_text":     "{{lead.custom_field.model_adi}}",
    "channel":        "whatsapp",
    "include_context": true
  }

Yanıt İşleme:
  price.status == "success"         → price.reply_text gönder
                                      model_key = price.model_key kaydet
  price.status == "multiple_matches" → price.reply_text gönder (seçim sor)
  price.status == "not_found"        → danışmana yönlendir

AI Entegrasyonu için:
  chatbot_context alanını AI system prompt'una ekleyin:
  - chatbot_context.welcome, faq, service_info, installment_info...`;

  const curlExample = `# Tek API çağrısı testi
curl -X POST ${apiUrl}/api/bot \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model_text": "vrs 125",
    "channel": "whatsapp",
    "include_context": true
  }'

# Stabil model_key ile (önerilen)
curl -X POST ${apiUrl}/api/bot \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"model_key": "vrs125", "channel": "whatsapp"}'`;

  return (
    <Layout>
      <div className="p-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 bg-[#111827] rounded-md flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-4xl font-bold uppercase text-gray-900 tracking-tight"
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
              Entegrasyon Rehberi
            </h1>
          </div>
          <p className="text-sm text-gray-500 ml-12">Kommo Salesbot, n8n ve Zapier entegrasyonu için adım adım kılavuz</p>
        </div>

        {/* API Base URL */}
        <div className="mb-8 p-4 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase text-gray-500 tracking-wider mb-1">API Base URL</p>
            <code className="text-sm font-mono text-gray-800">{apiUrl}/api</code>
          </div>
          <div className="text-right">
            <p className="text-xs font-bold uppercase text-gray-500 tracking-wider mb-1">Auth</p>
            <code className="text-xs font-mono text-gray-600">Authorization: Bearer API_KEY</code>
          </div>
        </div>

        {/* Section 1: API Endpoints */}
        <Section num="1" title="Tek API Çağrısı — Her Şey Bir Arada" icon={Code2}>
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
            <p className="text-sm text-green-800 font-semibold">
              <code className="bg-green-100 px-1.5 py-0.5 rounded">POST /api/bot</code> — fiyat sonucu + chatbot AI içeriği tek çağrıda döner.
              Kommo / n8n'de sadece bu endpoint'i kullanın.
            </p>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-xs font-bold uppercase text-gray-500 tracking-wider mb-2">İstek (Request)</p>
              <CodeBlock code={priceRequestBody} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs font-bold uppercase text-green-600 tracking-wider mb-2">✅ Bulundu (success)</p>
                <CodeBlock code={priceSuccessResp} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase text-amber-600 tracking-wider mb-2">⚠️ Çoklu Sonuç (multiple_matches)</p>
                <CodeBlock code={priceMultipleResp} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase text-red-600 tracking-wider mb-2">❌ Bulunamadı (not_found)</p>
                <CodeBlock code={priceNotFoundResp} />
              </div>
            </div>
          </div>
        </Section>

        {/* Section 2: Model ID Caching */}
        <Section num="2" title="Model Key ile Sorgulama (Önerilen!)" icon={Database}>
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
            <p className="text-sm text-green-800 font-medium">
              <strong>model_key kullanın</strong> — liste değişse de model_key sabit kalır.
              <code className="bg-green-100 px-1 rounded ml-1">item_id</code> yeni liste oluşturulduğunda değişmeyecek şekilde korunur, ama <code className="bg-green-100 px-1 rounded">model_key</code> her zaman çalışır.
            </p>
          </div>
          <CodeBlock code={`{
  "model_key": "vrs125",        // Daima sabit — liste güncellenirse de çalışır
  "channel": "whatsapp"
}`} lang="json" />
          <CodeBlock code={priceByIdBody} lang="json" />
          <div className="mt-4 p-3 bg-gray-50 rounded-md">
            <p className="text-xs font-semibold text-gray-600 mb-2">Kommo Custom Field Önerisi:</p>
            <div className="flex flex-col gap-1 text-xs font-mono text-gray-500">
              <span>• <strong>model_key</strong> (text) — vrs125, newcity50, borelli... <span className="text-green-600 font-bold">Önerilen</span></span>
              <span>• <strong>model_id</strong> (text) — API'dan dönen item_id (yedek)</span>
            </div>
          </div>
        </Section>

        {/* Section 3: N8N Flow */}
        <Section num="3" title="N8N / Make Akış Diyagramı" icon={ArrowRight}>
          <CodeBlock code={n8nFlow} lang="text" />
        </Section>

        {/* Section 4: Kommo Salesbot */}
        <Section num="4" title="Kommo Salesbot Ayarları" icon={Bot}>
          <CodeBlock code={kommoSalesbot} lang="text" />
          <div className="mt-4 space-y-3">
            <div className="p-3 bg-blue-50 border border-blue-100 rounded-md text-sm text-blue-700">
              <strong>multiple_matches durumunda:</strong> Müşteriye numaralı liste gönderin. Yanıta göre ilgili modelin <code className="bg-blue-100 px-1 rounded">item_id</code>'sini seçin ve model_id alanına kaydedin.
            </div>
            <div className="p-3 bg-green-50 border border-green-100 rounded-md text-sm text-green-700">
              <strong>İpucu:</strong> <code className="bg-green-100 px-1 rounded">reply_text</code> alanını doğrudan WhatsApp/Instagram mesajı olarak gönderebilirsiniz. Emoji ve fiyat formatlaması zaten yapılmış.
            </div>
          </div>
        </Section>

        {/* Section 5: Test */}
        <Section num="5" title="CURL ile Test" icon={Code2}>
          <CodeBlock code={curlExample} lang="bash" />
          <div className="mt-4 p-3 bg-gray-50 rounded-md text-xs text-gray-500">
            API anahtarını <strong>API Anahtarları</strong> sayfasından oluşturun ve buraya yapıştırın.
          </div>
        </Section>

        {/* Status Summary */}
        <div className="mt-2 bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-700"
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
              API Yanıt Durumları Özeti
            </h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs font-bold uppercase text-gray-500 border-b bg-gray-50">
                <th className="px-4 py-2.5 text-left">status</th>
                <th className="px-4 py-2.5 text-left">Açıklama</th>
                <th className="px-4 py-2.5 text-left">Bot Aksiyonu</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["success",          "Model bulundu, fiyat döndü",                   "price.reply_text gönder + model_key kaydet"],
                ["multiple_matches", "Çoklu versiyon (newlight50, newlight125)",      "price.reply_text gönder (seçim listesi), cevaba göre model_key kaydet"],
                ["not_found",        "Model bulunamadı (fuzzy de eşleşmedi)",        "Satış danışmanına yönlendir + 'Model Eşleştirme' sayfasına bak"],
                ["error",            "Aktif fiyat listesi yok",                       "Yöneticiyi bilgilendir"],
              ].map(([status, desc, action]) => (
                <tr key={status} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-2.5">
                    <code className={`text-xs font-mono font-bold px-1.5 py-0.5 rounded ${
                      status === "success" ? "bg-green-50 text-green-700" :
                      status === "multiple_matches" ? "bg-amber-50 text-amber-700" :
                      status === "not_found" ? "bg-red-50 text-red-600" :
                      "bg-gray-100 text-gray-600"
                    }`}>{status}</code>
                  </td>
                  <td className="px-4 py-2.5 text-gray-600 text-xs">{desc}</td>
                  <td className="px-4 py-2.5 text-gray-700 text-xs font-medium">{action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
